import { useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
    RiTruckLine,
    RiStore2Line,
    RiAddLine,
    RiMapPinLine,
    RiMailLine,
    RiPhoneLine,
    RiPrinterLine,
    RiCheckLine,
} from "react-icons/ri";
import ProposalProvinceField from "./ProposalProvinceField";
import { sanitizeText } from "../../../../utils/TextUtils";

const ProposalOrderForm = ({
    formData,
    onFormChange,
    provincesList = [],
    addressMode = "existing",
    onAddressModeChange,
    savedAddresses = [],
    selectedAddressId = "primary",
    onSelectSavedAddress,
    errors = {},
    validationTrigger = 0,
}) => {
    const isExistingAddress = addressMode === "existing";

    // Active saved address (when in existing address mode)
    const activeSavedAddress = useMemo(() => {
        if (!isExistingAddress || !Array.isArray(savedAddresses) || savedAddresses.length === 0) {
            return null;
        }
        return (
            savedAddresses.find((addr, idx) => String(addr?.id ?? idx) === String(selectedAddressId ?? 0)) ||
            savedAddresses[0] ||
            null
        );
    }, [isExistingAddress, savedAddresses, selectedAddressId]);

    // Smart Read-Only check: Read-only ONLY if existing mode AND value was already present in saved address
    const isFieldReadOnly = (fieldName) => {
        if (!isExistingAddress || !activeSavedAddress) return false;
        const shortKeyMap = {
            address_title: "address_title",
            destination_address: "address",
            destination_city: "city",
            destination_postal_code: "postal_code",
            destination_province: "province",
            destination_phone: "telephone",
            destination_mobile_phone: "mobile_no",
            destination_email: "email",
            destination_fax: "fax",
        };
        const shortKey = shortKeyMap[fieldName];
        const val = activeSavedAddress[fieldName] || (shortKey && activeSavedAddress[shortKey]);
        return Boolean(val && String(val).trim().length > 0);
    };

    // Individual refs for mandatory input fields
    const addressTitleRef = useRef(null);
    const addressRef = useRef(null);
    const cityRef = useRef(null);
    const postalCodeRef = useRef(null);
    const phoneRef = useRef(null);
    const mobilePhoneRef = useRef(null);
    const emailRef = useRef(null);
    const faxRef = useRef(null);

    // Keep errors ref updated so the effect always reads fresh errors without re-triggering on typing
    const errorsRef = useRef(errors);
    errorsRef.current = errors;

    // Auto-focus and scroll to first error field ONLY when validation is triggered on save
    useEffect(() => {
        if (!validationTrigger || addressMode === "existing") return;
        const currentErrors = errorsRef.current;
        if (!currentErrors || Object.keys(currentErrors).length === 0) return;

        const refMap = {
            address_title: addressTitleRef,
            destination_address: addressRef,
            destination_city: cityRef,
            destination_postal_code: postalCodeRef,
            destination_phone: phoneRef,
            destination_mobile_phone: mobilePhoneRef,
            destination_email: emailRef,
            destination_fax: faxRef,
        };

        const fieldOrder = [
            "address_title",
            "destination_address",
            "destination_city",
            "destination_postal_code",
            "destination_province",
            "destination_phone",
            "destination_mobile_phone",
            "destination_email",
            "destination_fax",
        ];

        const firstErrorKey = fieldOrder.find((key) => Boolean(currentErrors[key]));
        if (firstErrorKey && refMap[firstErrorKey]?.current) {
            const el = refMap[firstErrorKey].current;
            el.focus({ preventScroll: true });
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [validationTrigger, addressMode]);

    return (
        <div className="w-full bg-[#FBFBFB] border-[2px] border-[#8B24332B] rounded-xl shadow-xs relative mb-6">
            {/* Unified Top Header with Segmented Switch */}
            <div className="px-5 py-4 border-b border-[#8B2433]/20 bg-[#FAF3F4] rounded-t-[10px] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <RiTruckLine className="w-5 h-5 text-[#8B2433] shrink-0" />
                    <div>
                        <h3 className="text-lg font-bold poppins-medium text-[#303030]">
                            Destinazione Merce
                        </h3>
                    </div>
                </div>

                {/* Segmented Pill Switch */}
                <div className="inline-flex items-center p-1 bg-white border border-gray-200 rounded-full shadow-2xs self-start sm:self-auto shrink-0">
                    <button
                        type="button"
                        onClick={() => onAddressModeChange && onAddressModeChange("existing")}
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs poppins-medium rounded-full transition-all duration-200 cursor-pointer ${
                            addressMode === "existing"
                                ? "bg-[#8B2433] text-white shadow-xs"
                                : "text-[#6B6B6B] hover:text-[#303030] hover:bg-gray-100"
                        }`}
                    >
                        <RiStore2Line className="w-3.5 h-3.5" />
                        <span>Indirizzo Esistente</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => onAddressModeChange && onAddressModeChange("new")}
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs poppins-medium rounded-full transition-all duration-200 cursor-pointer ${
                            addressMode === "new"
                                ? "bg-[#8B2433] text-white shadow-xs"
                                : "text-[#6B6B6B] hover:text-[#303030] hover:bg-gray-100"
                        }`}
                    >
                        <RiAddLine className="w-3.5 h-3.5" />
                        <span>Nuovo Indirizzo</span>
                    </button>
                </div>
            </div>

            <div className="p-5 sm:p-6 space-y-6">
                {/* Delivery Address Details */}
                <div>
                    {/* Existing Addresses Summary Cards Grid */}
                    {addressMode === "existing" && (
                        savedAddresses && savedAddresses.length > 0 ? (
                            <div className="p-4 sm:p-5 bg-gradient-to-b from-[#FCF9F9] to-[#FAF3F4] border-[1.5px] border-[#8B2433]/20 rounded-2xl shadow-2xs">
                                <div className="flex items-center justify-between mb-3.5">
                                    <div className="flex items-center gap-2">
                                        <RiStore2Line className="w-4 h-4 text-[#8B2433]" />
                                        <span className="text-xs font-bold poppins-semibold text-[#303030]">
                                            {savedAddresses.length > 1
                                                ? "Seleziona Sede di Destinazione Esistente"
                                                : "Riepilogo Indirizzo Registrato"}
                                        </span>
                                    </div>
                                    <span className="text-[11px] text-[#8B2433] poppins-medium bg-white/95 px-2.5 py-0.5 rounded-full border border-[#8B2433]/20 shadow-2xs">
                                        {savedAddresses.length} {savedAddresses.length === 1 ? "indirizzo disponibile" : "indirizzi disponibili"}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                                    {savedAddresses.map((addr, idx) => {
                                        const addressKey = addr?.id ?? idx;
                                        const isSelected = String(selectedAddressId ?? 0) === String(addressKey);

                                        return (
                                            <div
                                                key={addressKey}
                                                onClick={() => onSelectSavedAddress && onSelectSavedAddress(addr, idx)}
                                                className={`group relative p-4 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col justify-start ${
                                                    isSelected
                                                        ? "bg-gradient-to-br from-white via-[#FCF9FA] to-[#FAF2F4] border-[#8B2433] shadow-[0_6px_20px_rgba(139,36,51,0.08)] ring-1 ring-[#8B2433]/25"
                                                        : "bg-white/90 border-gray-200/80 hover:border-[#8B2433]/40 hover:bg-white hover:shadow-xs"
                                                }`}
                                            >
                                                {/* Header badge & radio/check indicator */}
                                                <div className="flex items-center justify-between gap-2 mb-2.5 pb-2 border-b border-[#8B2433]/10">
                                                    <div className="flex items-center gap-2 min-w-0 pr-1">
                                                        <span className={`w-2 h-2 rounded-full shrink-0 transition-transform ${
                                                            isSelected ? "bg-[#8B2433] scale-110" : "bg-gray-300 group-hover:bg-[#8B2433]/50"
                                                        }`} />
                                                        <span className="text-xs font-bold poppins-medium text-[#2E2E2E] truncate">
                                                            {addr?.address_title || "Indirizzo"}
                                                        </span>
                                                        {addr?.is_legal_default && (
                                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FAF0F2] to-[#F5E6E8] text-[#8B2433] border border-[#8B2433]/20 font-semibold poppins-medium shrink-0 shadow-2xs">
                                                                Predefinita
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center transition-all duration-200 shrink-0 ${
                                                        isSelected
                                                            ? "border-[#8B2433] bg-[#8B2433] shadow-xs ring-2 ring-[#8B2433]/20"
                                                            : "border-gray-300 bg-white group-hover:border-[#8B2433]/50"
                                                    }`}>
                                                        {isSelected && <RiCheckLine className="w-3 h-3 text-white stroke-[0.5]" />}
                                                    </div>
                                                </div>

                                                {/* Address Details */}
                                                <div className="space-y-1.5 text-xs text-[#505050] poppins-regular">
                                                    <div className="flex items-start gap-2 text-gray-600 text-[11.5px] leading-relaxed pt-0.5">
                                                        <div className="w-4.5 h-4.5 rounded-md bg-[#8B2433]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#8B2433]">
                                                            <RiMapPinLine className="w-3 h-3" />
                                                        </div>
                                                        <span className="line-clamp-2">
                                                            {addr?.formatted_full_address}
                                                        </span>
                                                    </div>

                                                    {/* Email */}
                                                    {addr?.email && (
                                                        <div className="flex items-center gap-2 text-gray-500 text-[11px] pt-0.5">
                                                            <div className="w-4.5 h-4.5 rounded-md bg-gray-100 flex items-center justify-center shrink-0 text-gray-500">
                                                                <RiMailLine className="w-3 h-3" />
                                                            </div>
                                                            <span className="font-medium text-gray-600 truncate">
                                                                {addr.email}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Contact numbers: telephone & mobile_no */}
                                                    {(() => {
                                                        const contacts = [
                                                            addr?.telephone,
                                                            addr?.mobile_no,
                                                        ]
                                                            .map((v) => (v ? String(v).trim() : ""))
                                                            .filter(Boolean);

                                                        if (contacts.length === 0) return null;

                                                        return (
                                                            <div className="flex items-center gap-2 text-gray-500 text-[11px] pt-1">
                                                                <div className="w-4.5 h-4.5 rounded-md bg-gray-100 flex items-center justify-center shrink-0 text-gray-500">
                                                                    <RiPhoneLine className="w-3 h-3" />
                                                                </div>
                                                                <span className="font-medium text-gray-600 truncate">
                                                                    {contacts.join(", ")}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* Fax */}
                                                    {addr?.fax && (
                                                        <div className="flex items-center gap-2 text-gray-500 text-[11px] pt-0.5">
                                                            <div className="w-4.5 h-4.5 rounded-md bg-gray-100 flex items-center justify-center shrink-0 text-gray-500">
                                                                <RiPrinterLine className="w-3 h-3" />
                                                            </div>
                                                            <span className="font-medium text-gray-600 truncate">
                                                                Fax: {addr.fax}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="p-6 text-center text-xs text-gray-500 poppins-medium bg-white rounded-2xl border border-dashed border-gray-300">
                                Nessun indirizzo registrato disponibile per questo cliente.
                            </div>
                        )
                    )}

                    {/* New Address Input Form Fields (Only shown in 'new' address mode) */}
                    {addressMode === "new" && (
                        <div>
                            {/* Row 1: Address Title + Indirizzo + Città */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                                {/* Address Title */}
                                <div className="flex flex-col gap-1.5 md:col-span-4">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Address Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={addressTitleRef}
                                        type="text"
                                        name="address_title"
                                        placeholder="Address Title"
                                        value={sanitizeText(formData.address_title || "")}
                                        onChange={onFormChange}
                                        readOnly={isFieldReadOnly("address_title")}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("address_title")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.address_title
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("address_title") && errors.address_title && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.address_title}
                                        </span>
                                    )}
                                </div>

                                {/* Indirizzo */}
                                <div className="flex flex-col gap-1.5 md:col-span-4">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Indirizzo <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={addressRef}
                                        type="text"
                                        name="destination_address"
                                        placeholder="Indirizzo (Via / Piazza e civico)"
                                        value={sanitizeText(formData.destination_address || "")}
                                        onChange={onFormChange}
                                        readOnly={isFieldReadOnly("destination_address")}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_address")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_address
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_address") && errors.destination_address && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_address}
                                        </span>
                                    )}
                                </div>

                                {/* Città */}
                                <div className="flex flex-col gap-1.5 md:col-span-4">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Città <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={cityRef}
                                        type="text"
                                        name="destination_city"
                                        placeholder="Città"
                                        value={sanitizeText(formData.destination_city || "")}
                                        onChange={onFormChange}
                                        readOnly={isFieldReadOnly("destination_city")}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_city")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_city
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_city") && errors.destination_city && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_city}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: CAP (2 cols) + Provincia (4 cols) + Telefono (3 cols) + Cellulare (3 cols) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 mb-4 relative z-30">
                                {/* CAP */}
                                <div className="flex flex-col gap-1.5 sm:col-span-1 md:col-span-2">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        CAP <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={postalCodeRef}
                                        type="text"
                                        name="destination_postal_code"
                                        placeholder="Cap"
                                        maxLength={10}
                                        value={sanitizeText(formData.destination_postal_code || "")}
                                        onChange={onFormChange}
                                        readOnly={isFieldReadOnly("destination_postal_code")}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_postal_code")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_postal_code
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_postal_code") && errors.destination_postal_code && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_postal_code}
                                        </span>
                                    )}
                                </div>

                                {/* Provincia */}
                                <div className="sm:col-span-1 md:col-span-4 relative z-40">
                                    <ProposalProvinceField
                                        selectedProvince={formData.destination_province || ""}
                                        onChange={onFormChange}
                                        provincesList={provincesList}
                                        disabled={isFieldReadOnly("destination_province")}
                                        error={errors.destination_province || ""}
                                    />
                                </div>

                                {/* Telefono */}
                                <div className="flex flex-col gap-1.5 sm:col-span-1 md:col-span-3">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Telefono <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={phoneRef}
                                        type="text"
                                        name="destination_phone"
                                        placeholder="Telefono"
                                        value={sanitizeText(formData.destination_phone || "")}
                                        readOnly={isFieldReadOnly("destination_phone")}
                                        onChange={(e) => {
                                            if (isFieldReadOnly("destination_phone")) return;
                                            const value = e.target.value;
                                            if (/^\+?\d*$/.test(value) || value === "") {
                                                onFormChange(e);
                                            }
                                        }}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_phone")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_phone
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_phone") && errors.destination_phone && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_phone}
                                        </span>
                                    )}
                                </div>

                                {/* Cellulare */}
                                <div className="flex flex-col gap-1.5 sm:col-span-1 md:col-span-3">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Cellulare <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={mobilePhoneRef}
                                        type="text"
                                        name="destination_mobile_phone"
                                        placeholder="Cellulare"
                                        value={sanitizeText(formData.destination_mobile_phone || "")}
                                        readOnly={isFieldReadOnly("destination_mobile_phone")}
                                        onChange={(e) => {
                                            if (isFieldReadOnly("destination_mobile_phone")) return;
                                            const value = e.target.value;
                                            if (/^\+?\d*$/.test(value) || value === "") {
                                                onFormChange(e);
                                            }
                                        }}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_mobile_phone")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_mobile_phone
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_mobile_phone") && errors.destination_mobile_phone && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_mobile_phone}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Row 3: Email (8 cols) + Fax (4 cols) */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 relative z-10">
                                {/* Email */}
                                <div className="flex flex-col gap-1.5 md:col-span-8">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={emailRef}
                                        type="email"
                                        name="destination_email"
                                        placeholder="Email"
                                        value={sanitizeText(formData.destination_email || "")}
                                        onChange={onFormChange}
                                        readOnly={isFieldReadOnly("destination_email")}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_email")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_email
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_email") && errors.destination_email && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_email}
                                        </span>
                                    )}
                                </div>

                                {/* Fax */}
                                <div className="flex flex-col gap-1.5 md:col-span-4">
                                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                                        Fax <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        ref={faxRef}
                                        type="text"
                                        name="destination_fax"
                                        placeholder="Fax"
                                        value={sanitizeText(formData.destination_fax || "")}
                                        readOnly={isFieldReadOnly("destination_fax")}
                                        onChange={(e) => {
                                            if (isFieldReadOnly("destination_fax")) return;
                                            const value = e.target.value;
                                            if (/^\+?\d*$/.test(value) || /^\d*$/.test(value) || value === "") {
                                                onFormChange(e);
                                            }
                                        }}
                                        className={`h-[42px] poppins-regular px-4 text-xs rounded-3xl border w-full transition-colors ${
                                            isFieldReadOnly("destination_fax")
                                                ? "bg-[#F8F8F8] text-[#555] border-gray-200 cursor-not-allowed select-none focus:outline-none"
                                                : errors.destination_fax
                                                ? "text-[#303030] bg-red-50/20 border-red-500 focus:border-red-500 focus:outline-none"
                                                : "text-[#303030] bg-white border-gray-300 focus:border-[#d5d6d9] focus:bg-[#f6efef] focus:outline-none"
                                        }`}
                                    />
                                    {!isFieldReadOnly("destination_fax") && errors.destination_fax && (
                                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                                            {errors.destination_fax}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ProposalOrderForm.propTypes = {
    formData: PropTypes.object.isRequired,
    onFormChange: PropTypes.func.isRequired,
    provincesList: PropTypes.array,
    addressMode: PropTypes.oneOf(["existing", "new"]),
    onAddressModeChange: PropTypes.func,
    savedAddresses: PropTypes.array,
    selectedAddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectSavedAddress: PropTypes.func,
    errors: PropTypes.object,
    validationTrigger: PropTypes.number,
};

export default ProposalOrderForm;

